import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {Menu, X, LogIn, LogOut, User, Shield} from 'lucide-react'
import { lumi } from '../lib/lumi'

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [user, setUser] = useState<any>(null)
  const location = useLocation()

  useEffect(() => {
    const unsubscribe = lumi.auth.onAuthChange((currentUser) => {
      setUser(currentUser)
    })
    return () => unsubscribe()
  }, [])

  const links = [
    { path: '/', label: 'Home' },
    { path: '/sobre', label: 'Sobre' },
    { path: '/oracao', label: 'Oração' },
    { path: '/testemunhos', label: 'Testemunhos' },
    { path: '/palavra-de-deus', label: 'Palavra de Deus' },
    { path: '/escola-biblica', label: 'Escola Bíblica' },
    { path: '/eventos', label: 'Eventos' },
    { path: '/louvores', label: 'Louvores' },
    { path: '/programacao', label: 'Programação' },
    { path: '/contato', label: 'Contato' }
  ]

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src="https://static.lumi.new/material/fd/fd208cdb7c6b89103dc6a3e704c4cafe.png" 
              alt="REFUGE IN CHRIST MINISTRY" 
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-1">
            {links.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#1e3a8a] bg-[#6b8f71]/10'
                    : 'text-gray-700 hover:text-[#1e3a8a] hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Admin Link - Always Visible */}
            <Link
              to="/admin"
              className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-[#1e3a8a] hover:bg-[#6b8f71]/10 transition-colors"
            >
              <Shield size={18} />
              <span className="hidden xl:inline">Admin</span>
            </Link>

            {/* Auth Section */}
            <div className="ml-4 pl-4 border-l border-gray-200">
              {user ? (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <User size={16} />
                    <span className="hidden xl:inline">{user.userName}</span>
                  </div>
                  <button
                    onClick={() => lumi.auth.signOut()}
                    className="flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-gray-700 hover:text-[#1e3a8a] hover:bg-gray-50 transition-colors"
                  >
                    <LogOut size={18} />
                    <span className="hidden xl:inline">Sair</span>
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => lumi.auth.signIn()}
                  className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-[#1e3a8a] hover:bg-[#2d4ba5] transition-colors"
                >
                  <LogIn size={18} />
                  Entrar
                </button>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {links.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className={`block px-3 py-2 rounded-md text-base font-medium ${
                  location.pathname === link.path
                    ? 'text-[#1e3a8a] bg-[#6b8f71]/10'
                    : 'text-gray-700 hover:text-[#1e3a8a] hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Admin Link Mobile - Always Visible */}
            <Link
              to="/admin"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-md text-base font-medium text-[#1e3a8a] hover:bg-[#6b8f71]/10"
            >
              <Shield size={20} />
              Admin
            </Link>

            {/* Auth Section Mobile */}
            <div className="pt-4 mt-4 border-t border-gray-200">
              {user ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700">
                    <User size={18} />
                    {user.userName}
                  </div>
                  <button
                    onClick={() => {
                      lumi.auth.signOut()
                      setIsOpen(false)
                    }}
                    className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-[#1e3a8a] hover:bg-gray-50"
                  >
                    <LogOut size={20} />
                    Sair
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    lumi.auth.signIn()
                    setIsOpen(false)
                  }}
                  className="flex items-center gap-2 w-full px-4 py-2 rounded-md text-base font-medium text-white bg-[#1e3a8a] hover:bg-[#2d4ba5]"
                >
                  <LogIn size={20} />
                  Entrar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  )
}

export default Navbar
